import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types';
import {
  Choice,
  INITIAL_CHOICE,
  INITIAL_INTERESTED_PRODUCT_LINE,
  InterestedProductLine,
  ProductInfo,
  RateStatistic,
} from 'types/electionService';

const createInitialInterestedProductLinesList = (
  dependentIds: string[],
  productLineIds: ProductLineId[],
): InterestedProductLine[] => {
  if (productLineIds.length === 0) {
    return [];
  }

  return productLineIds.map((productLineId) => {
    return {
      ...INITIAL_INTERESTED_PRODUCT_LINE,
      productLineId,
      dependentIds,
    };
  });
};

/**
 * Builds initial product line interest by merging domain default product lines with saved selections.
 */
export const buildInterestedProductLinesForSubscriber = (
  interestedProductLines: InterestedProductLine[],
  dependentIds: string[],
  productLineIds: ProductLineId[],
): InterestedProductLine[] => {
  const initialInterestedProductLinesList = createInitialInterestedProductLinesList(dependentIds, productLineIds);
  const populatedInterestedProductLines = interestedProductLines ?? [];

  const interestedProductLinesByProductLine = new Map(
    populatedInterestedProductLines.map((interestedProductLine) => [
      interestedProductLine.productLineId,
      interestedProductLine,
    ]),
  );

  return initialInterestedProductLinesList.map((initialInterestedProductLine) => {
    const existing = interestedProductLinesByProductLine.get(initialInterestedProductLine.productLineId);
    if (!existing) return initialInterestedProductLine;

    return {
      ...initialInterestedProductLine,
      value: existing.value,
      dependentIds: existing.dependentIds,
    };
  });
};

const createInitialChoicesList = (
  dependentIds: string[],
  products: ProductInfo[],
  rateStats: RateStatistic | null,
): Choice[] => {
  if (products.length === 0) {
    return [];
  }

  return products.map(({ productLineId }) => {
    // productLine with 1 tier needs dependentIds as empty array
    const hasProductLineOneTier = rateStats ? Object.keys(rateStats?.[productLineId] || []).length === 1 : false;
    return { ...INITIAL_CHOICE, productLineId, dependentIds: hasProductLineOneTier ? [] : dependentIds };
  });
};

/**
 * Builds initial choices by merging default product lines from available products with saved selections.
 */
export const buildChoicesForSubscriber = (
  choice: Choice[],
  dependentIds: string[],
  products: ProductInfo[],
  rateStats: RateStatistic | null,
): Choice[] => {
  const initialChoices = createInitialChoicesList(dependentIds, products, rateStats);
  const populatedChoices = choice ?? [];

  const choicesByProductLine = new Map(populatedChoices.map((item) => [item.productLineId, item]));

  return initialChoices.map((initialChoice) => ({
    ...initialChoice,
    ...choicesByProductLine.get(initialChoice.productLineId),
  }));
};
